import { useMemo } from "react";
import { Alert } from "@mui/material";
import { formatINR } from "@/utils/app-formatters";
import {
  DEFAULT_MUTTAVATTEEN_OUTSTANDING_MIN_PERCENT,
  MUTTAVATTEEN_OUTSTANDING_NOTIFY_HOLDER_ONLY,
} from "./utils";
import { usePreviewMuttavatteenOutstandingRecipients } from "./hooks";
import { formatSabilOutstandingTypeLabel } from "./sabil-outstanding-type-label";
import OutstandingRecipientPanel, {
  type OutstandingRecipientColumn,
  type OutstandingRecipientPanelProps,
} from "./OutstandingRecipientPanel";

type MuttavatteenOutstandingRecipientPanelProps = Pick<
  OutstandingRecipientPanelProps,
  "onPreviewCount" | "onPreviewed" | "onSelectionChange"
>;

export default function MuttavatteenOutstandingRecipientPanel(
  props: MuttavatteenOutstandingRecipientPanelProps
) {
  const columns = useMemo(
    () => (): OutstandingRecipientColumn[] => [
      {
        key: "recipient",
        header: "Recipient",
        render: (r) => String(r.recipientName ?? "—"),
        compactGroup: "identity",
      },
      {
        key: "its",
        header: "ITS",
        render: (r) => String(r.itsNo ?? "—"),
        compactGroup: "identity",
      },
      {
        key: "phone",
        header: "Phone",
        render: (r) => String(r.phone ?? "—"),
        compactGroup: "identity",
      },
      {
        key: "sabil",
        header: "Sabil",
        render: (r) => String(r.name ?? "—"),
        compactGroup: "identity",
      },
      {
        key: "type",
        header: "Type",
        render: (r) => formatSabilOutstandingTypeLabel(r.sabilType),
        compactGroup: "identity",
      },
      {
        key: "due",
        header: "Due",
        align: "right",
        render: (r) => formatINR(r.takhmeenAmount, { empty: "—" }),
      },
      {
        key: "paid",
        header: "Paid",
        align: "right",
        render: (r) => formatINR(r.paidBalance, { empty: "—" }),
      },
      {
        key: "pending",
        header: "Pending",
        align: "right",
        render: (r) => formatINR(r.pendingBalance, { empty: "—" }),
      },
      {
        key: "outstandingPct",
        header: "Outstanding %",
        align: "right",
        render: (r) =>
          r.outstandingPercent != null ? `${Number(r.outstandingPercent).toFixed(1)}%` : "—",
      },
    ],
    []
  );

  return (
    <OutstandingRecipientPanel
      {...props}
      sabilTypeDefault="MUTTAVATTEEN"
      usePreviewRecipients={usePreviewMuttavatteenOutstandingRecipients}
      applyFieldDefaults={({ minOutstandingPercent, setValue }) => {
        if (minOutstandingPercent == null) {
          setValue("minOutstandingPercent", DEFAULT_MUTTAVATTEEN_OUTSTANDING_MIN_PERCENT, {
            shouldDirty: false,
          });
        }
        setValue("notifyMode", MUTTAVATTEEN_OUTSTANDING_NOTIFY_HOLDER_ONLY, { shouldDirty: false });
      }}
      introText={(sabilType) => (
        <>
          Reminds open {formatSabilOutstandingTypeLabel(sabilType)} accounts with outstanding
          balances, sent to the holder WhatsApp number on the sabil record. Switch tabs to send the
          other Mutawatteen period separately — the two are never mixed in one broadcast.
        </>
      )}
      itsAutocompleteLabel="Holder ITS"
      minPercentHelperText="Include accounts with pending balance and outstanding % at or above this value (default 1)."
      renderNotifyModeSection={() => (
        <Alert severity="info" sx={{ mt: 1 }}>
          Notify mode is fixed to <strong>holder WhatsApp only</strong> (
          {MUTTAVATTEEN_OUTSTANDING_NOTIFY_HOLDER_ONLY}).
        </Alert>
      )}
      describeSendMode={() => "holder WhatsApp only"}
      columns={columns}
      getRowKey={(row, index) =>
        `${String(row.sabilId ?? "")}-${String(row.itsNo ?? row.phone ?? "")}-${index}`
      }
      extractRowItsId={(row) => row.itsNo}
    />
  );
}
